
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Button, Card, Dimmer, Divider, Dropdown, Form, Grid,
  Header, Icon, Input, Label, Loader, Message, Segment,
  Table, Tab
} from "semantic-ui-react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS, ArcElement, Tooltip, Legend,
  CategoryScale, LinearScale, BarElement, Title
} from "chart.js";
import * as XLSX from "xlsx";
import { db } from "../firebase/firebaseConfig";
import { UserContext } from "../context/UserContext";

ChartJS.register(
  ArcElement, Tooltip, Legend, CategoryScale,
  LinearScale, BarElement, Title
);

const UI = {
  bg: "#f8fafc",
  border: "#e5e9ef",
  navy: "#172b4d",
  muted: "#66758c",
  teal: "#0d9da5"
};

const cardStyle = {
  background: "#fff",
  border: `1px solid ${UI.border}`,
  borderRadius: 9,
  boxShadow: "0 2px 8px rgba(23,43,77,.04)",
  marginBottom: 16
};

const normalize = (v) =>
  String(v || "").trim().toUpperCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const toDate = (v) => {
  if (!v) return null;
  if (typeof v.toDate === "function") return v.toDate();
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

const dateInput = (v) => {
  const d = toDate(v);
  if (!d) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const parseLocalDate = (v) => {
  if (!v) return null;
  const [y, m, d] = v.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const startOfDay = (d) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate());

const endOfDay = (d) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

const formatCL = (v, onlyDate = false) => {
  const d = toDate(v);
  if (!d) return "";
  return new Intl.DateTimeFormat("es-CL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...(onlyDate ? {} : { hour: "2-digit", minute: "2-digit" })
  }).format(d);
};

const presetRange = (key) => {
  const now = new Date();
  const from = startOfDay(now);
  const to = endOfDay(now);

  if (key === "TODO") return { from: null, to: null };
  if (key === "MES") {
    return {
      from: new Date(now.getFullYear(), now.getMonth(), 1),
      to
    };
  }
  if (key === "7D") from.setDate(from.getDate() - 6);
  if (key === "30D") from.setDate(from.getDate() - 29);
  return { from, to };
};

const rangeOptions = [
  { key: "HOY", value: "HOY", text: "Hoy" },
  { key: "7D", value: "7D", text: "Últimos 7 días" },
  { key: "30D", value: "30D", text: "Últimos 30 días" },
  { key: "MES", value: "MES", text: "Este mes" },
  { key: "TODO", value: "TODO", text: "Todo histórico" }
];

const maintenanceOptions = [
  { key: "TODAS", value: "TODAS", text: "Todas" },
  { key: "VENCIDAS_30D", value: "VENCIDAS_30D", text: "Vencidas y próximos 30 días" },
  { key: "VENCIDAS", value: "VENCIDAS", text: "Vencidas" },
  { key: "HOY", value: "HOY", text: "Vencen hoy" },
  { key: "7D", value: "7D", text: "Próximos 7 días" },
  { key: "15D", value: "15D", text: "Próximos 15 días" },
  { key: "30D", value: "30D", text: "Próximos 30 días" },
  { key: "MES", value: "MES", text: "Vencen este mes" }
];

const palette = [
  "#328AC4", "#0d9da5", "#21BA45", "#F2711C",
  "#A333C8", "#DB2828", "#6435C9", "#FBBD08",
  "#00B5AD", "#767676"
];

const estadoColors = {
  "POR CONFIRMAR": "#767676",
  INGRESADO: "#2185D0",
  ASIGNADO: "#F2711C",
  REALIZADO: "#6435C9",
  ENTREGADO: "#21BA45",
  ANULADO: "#DB2828"
};

const money = (n) =>
  `$${Math.round(Number(n) || 0).toLocaleString("es-CL")}`;

const unique = (arr) =>
  [...new Set(arr.map((v) => String(v || "").trim()))]
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, "es"));

const optionList = (arr) =>
  arr.map((v) => ({ key: v, value: v, text: v }));

const exportWorkbook = (rows, sheet, filename) => {
  if (!rows.length) return;
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = Object.keys(rows[0]).map((key) => ({
    wch: Math.min(36, Math.max(16, key.length + 5))
  }));
  XLSX.utils.book_append_sheet(wb, ws, sheet);
  XLSX.writeFile(wb, filename);
};

const SectionTitle = ({ icon, title, subtitle, action }) => (
  <Grid stackable verticalAlign="middle" style={{ marginBottom: 8 }}>
    <Grid.Column width={action ? 11 : 16}>
      <Header as="h3" style={{ color: UI.navy, margin: 0 }}>
        <Icon name={icon} color="teal" />
        <Header.Content>
          {title}
          {subtitle && (
            <Header.Subheader style={{ color: UI.muted, marginTop: 5 }}>
              {subtitle}
            </Header.Subheader>
          )}
        </Header.Content>
      </Header>
    </Grid.Column>
    {action && (
      <Grid.Column width={5} textAlign="right">
        {action}
      </Grid.Column>
    )}
  </Grid>
);

const KpiCard = ({ label, value, icon, color = "teal", detail }) => (
  <Card fluid style={{ ...cardStyle, marginBottom: 0, boxShadow: "none" }}>
    <Card.Content>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 8
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{
            color: UI.muted,
            fontSize: 12,
            fontWeight: 600
          }}>
            {label}
          </div>
          <div style={{
            fontSize: 25,
            fontWeight: 700,
            color: UI.navy,
            marginTop: 9,
            overflowWrap: "anywhere"
          }}>
            {value}
          </div>
          {detail && (
            <div style={{
              color: UI.muted,
              fontSize: 12,
              marginTop: 5
            }}>
              {detail}
            </div>
          )}
        </div>
        <Icon
          name={icon}
          color={color}
          size="large"
          style={{ margin: 0 }}
        />
      </div>
    </Card.Content>
  </Card>
);

const ChartPanel = ({ title, empty, children, height = 310 }) => (
  <Segment style={{ ...cardStyle, marginBottom: 0 }}>
    <Header as="h4" style={{ color: UI.navy, marginBottom: 18 }}>
      {title}
    </Header>
    <div style={{ height }}>
      {empty ? (
        <Message info content="Sin datos para graficar con estos filtros." />
      ) : children}
    </div>
  </Segment>
);

const StatusLabel = ({ value }) => {
  const colors = {
    ENTREGADO: "green",
    REALIZADO: "violet",
    ASIGNADO: "orange",
    INGRESADO: "blue",
    ANULADO: "red"
  };
  return (
    <Label size="small" color={colors[normalize(value)] || undefined}>
      {value || "Sin estado"}
    </Label>
  );
};

const MaintenanceLabel = ({ days }) => {
  let color = "teal";
  let label = `Vence en ${days} días`;

  if (days < 0) {
    color = "red";
    label = `Vencida hace ${Math.abs(days)} días`;
  } else if (days === 0) {
    color = "red";
    label = "Vence hoy";
  } else if (days <= 7) {
    color = "orange";
  } else if (days <= 30) {
    color = "yellow";
  }

  return <Label color={color} size="small">{label}</Label>;
};

const FilterDropdown = ({ label, value, onChange, values }) => (
  <Form.Field>
    <label>{label}</label>
    <Dropdown
      fluid
      selection
      clearable
      placeholder="Todos"
      value={value || ""}
      onChange={(_, data) => onChange(data.value || "")}
      options={optionList(values)}
    />
  </Form.Field>
);

const DateFilters = ({
  preset, from, to, onPreset, onFrom, onTo
}) => (
  <Form.Group widths="equal">
    <Form.Field>
      <label>Período</label>
      <Dropdown
        fluid
        selection
        value={preset}
        options={
          preset === "PERSONALIZADO"
            ? [
                {
                  key: "PERSONALIZADO",
                  value: "PERSONALIZADO",
                  text: "Personalizado"
                },
                ...rangeOptions
              ]
            : rangeOptions
        }
        onChange={(_, data) => onPreset(data.value)}
      />
    </Form.Field>
    <Form.Field>
      <label>Desde</label>
      <Input
        fluid
        type="date"
        value={dateInput(from)}
        onChange={(e) => onFrom(e.target.value)}
      />
    </Form.Field>
    <Form.Field>
      <label>Hasta</label>
      <Input
        fluid
        type="date"
        value={dateInput(to)}
        onChange={(e) => onTo(e.target.value)}
      />
    </Form.Field>
  </Form.Group>
);

const makeBar = (counts, byStatus = false, limit = 0) => {
  let entries = Object.entries(counts)
    .sort((a, b) => b[1] - a[1]);

  if (limit) entries = entries.slice(0, limit);

  const labels = entries.map(([name]) => name);
  const colors = labels.map((name, i) =>
    byStatus
      ? estadoColors[normalize(name)] || "#328AC4"
      : palette[i % palette.length]
  );

  return {
    data: {
      labels,
      datasets: [{
        label: "Órdenes",
        data: entries.map(([, n]) => n),
        backgroundColor: colors,
        borderRadius: 6,
        maxBarThickness: 48
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.raw} órdenes`
          }
        }
      },
      scales: {
        x: { grid: { display: false } },
        y: {
          beginAtZero: true,
          ticks: { precision: 0, stepSize: 1 }
        }
      }
    }
  };
};

const DashboardServicioTecnico = () => {
  const { users } = useContext(UserContext);
  const empId = users?.emp_id;

  const [docs, setDocs] = useState([]);
  const [presupuestosCab, setPresupuestosCab] = useState([]);
  const [presupuestosDet, setPresupuestosDet] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [preset, setPreset] = useState("30D");
  const [fromDate, setFromDate] = useState(() => presetRange("30D").from);
  const [toDateValue, setToDateValue] = useState(() => presetRange("30D").to);
  const [estado, setEstado] = useState("");
  const [estadoServicio, setEstadoServicio] = useState("");
  const [tecnico, setTecnico] = useState("");
  const [servicio, setServicio] = useState("");
  const [tipo, setTipo] = useState("");
  const [search, setSearch] = useState("");

  const [presetEconomico, setPresetEconomico] = useState("30D");
  const [desdeEconomico, setDesdeEconomico] = useState(
    () => presetRange("30D").from
  );
  const [hastaEconomico, setHastaEconomico] = useState(
    () => presetRange("30D").to
  );

  const [filtroMantencionST, setFiltroMantencionST] = useState("TODAS");
  const [buscarMantencionST, setBuscarMantencionST] = useState("");

  const load = useCallback(async () => {
    if (!empId) return;

    setLoading(true);
    setError("");

    try {
      const results = await Promise.allSettled([
        getDocs(query(
          collection(db, "ingresostcab"),
          where("emp_id", "==", empId)
        )),
        getDocs(query(
          collection(db, "presupuestoscab"),
          where("emp_id", "==", empId)
        )),
        getDocs(query(
          collection(db, "presupuestos"),
          where("emp_id", "==", empId)
        ))
      ]);

      const convert = (r) =>
        r.status === "fulfilled"
          ? r.value.docs.map((d) => ({ ...d.data(), id: d.id }))
          : [];

      setDocs(convert(results[0]));
      setPresupuestosCab(convert(results[1]));
      setPresupuestosDet(convert(results[2]));

      const labels = [
        "órdenes ST",
        "cabeceras de presupuestos",
        "detalle de presupuestos"
      ];

      const failed = results
        .map((r, i) => r.status === "rejected" ? labels[i] : null)
        .filter(Boolean);

      if (failed.length) {
        setError(`No fue posible cargar: ${failed.join(", ")}.`);
        results.forEach((r) => {
          if (r.status === "rejected") console.error(r.reason);
        });
      }
    } catch (e) {
      console.error("Dashboard ST:", e);
      setError("Error al consultar los datos.");
    } finally {
      setLoading(false);
    }
  }, [empId]);

  useEffect(() => {
    if (empId) {
      load();
    } else {
      setDocs([]);
      setPresupuestosCab([]);
      setPresupuestosDet([]);
    }
  }, [empId, load]);

  const changePreset = (value) => {
    const r = presetRange(value);
    setPreset(value);
    setFromDate(r.from);
    setToDateValue(r.to);
  };

  const changeEconomicPreset = (value) => {
    const r = presetRange(value);
    setPresetEconomico(value);
    setDesdeEconomico(r.from);
    setHastaEconomico(r.to);
  };

  const resetOperacion = () => {
    changePreset("30D");
    setEstado("");
    setEstadoServicio("");
    setTecnico("");
    setServicio("");
    setTipo("");
    setSearch("");
  };

  const resetEconomico = () => changeEconomicPreset("30D");

  const resetMantenciones = () => {
    setFiltroMantencionST("TODAS");
    setBuscarMantencionST("");
  };

  const options = useMemo(() => {
    const base = docs.filter((d) => normalize(d.estado) !== "ANULADO");
    return {
      estados: unique(base.map((d) => d.estado)),
      estadosServicio: unique(base.map((d) => d.estado_servicio)),
      tecnicos: unique(base.map((d) => d.tecnico)),
      servicios: unique(base.map((d) => d.servicio)),
      tipos: unique(base.map((d) => d.tipo))
    };
  }, [docs]);

  const filtered = useMemo(() => {
    const f0 = fromDate ? startOfDay(fromDate) : null;
    const f1 = toDateValue ? endOfDay(toDateValue) : null;
    const term = search.trim().toLowerCase();

    return docs.filter((d) => {
      if (normalize(d.estado) === "ANULADO") return false;

      const fecha = toDate(d.date);
      if (f0 && (!fecha || fecha < f0)) return false;
      if (f1 && (!fecha || fecha > f1)) return false;

      if (estado && d.estado !== estado) return false;
      if (estadoServicio && d.estado_servicio !== estadoServicio) return false;
      if (tecnico && d.tecnico !== tecnico) return false;
      if (servicio && d.servicio !== servicio) return false;
      if (tipo && d.tipo !== tipo) return false;

      if (!term) return true;

      return [
        d.folio, d.rut, d.entidad, d.correo,
        d.telefono, d.telefonos, d.direccion,
        d.familia, d.tipo, d.marca, d.modelo,
        d.serie, d.servicio, d.estado,
        d.estado_servicio, d.tecnico
      ].some((v) =>
        String(v ?? "").toLowerCase().includes(term)
      );
    });
  }, [
    docs, fromDate, toDateValue, estado,
    estadoServicio, tecnico, servicio, tipo, search
  ]);

  const agg = useMemo(() => {
    const total = filtered.length;

    const entregadas = filtered.filter(
      (d) => d.estado === "ENTREGADO"
    ).length;

    const enEjecucion = filtered.filter(
      (d) => normalize(d.estado_servicio).includes("EJECUT")
    ).length;

    const pendientes = filtered.filter(
      (d) => ["INGRESADO", "ASIGNADO"].includes(normalize(d.estado))
    ).length;

    const cerradas = filtered
      .map((d) => ({
        entrada: toDate(d.date),
        salida: toDate(d.fecha_out)
      }))
      .filter((d) => d.entrada && d.salida);

    const promCierre = cerradas.length
      ? cerradas.reduce((sum, d) =>
          sum + (d.salida.getTime() - d.entrada.getTime()) / 86400000,
          0
        ) / cerradas.length
      : 0;

    const count = (field) => filtered.reduce((obj, d) => {
      const key = String(d[field] || "SIN INFORMACIÓN").trim();
      obj[key] = (obj[key] || 0) + 1;
      return obj;
    }, {});

    return {
      total,
      entregadas,
      enEjecucion,
      pendientes,
      promCierre,
      pctEntregadas: total ? entregadas * 100 / total : 0,
      byEstado: count("estado"),
      byServicio: count("servicio"),
      byTecnico: count("tecnico")
    };
  }, [filtered]);

  const mantencionesST = useMemo(() => {
    const ultimaPorEquipo = new Map();

    docs.forEach((d) => {
      if (!normalize(d.servicio).includes("MANTENCION")) return;
      if (!["REALIZADO", "ENTREGADO"].includes(normalize(d.estado))) return;

      const entrega = toDate(d.fecha_out);
      if (!entrega) return;

      const key = d.eq_id || d.serie || d.id_eq || d.id;
      if (!key) return;

      const actual = ultimaPorEquipo.get(key);
      const fechaActual = actual ? toDate(actual.fecha_out) : null;

      if (!actual || !fechaActual || entrega > fechaActual) {
        ultimaPorEquipo.set(key, d);
      }
    });

    const hoy = startOfDay(new Date());

    return [...ultimaPorEquipo.values()].map((d) => {
      const entrega = toDate(d.fecha_out);
      const proxima = new Date(entrega);
      proxima.setFullYear(proxima.getFullYear() + 1);

      const diasRestantes = Math.round(
        (
          Date.UTC(
            proxima.getFullYear(),
            proxima.getMonth(),
            proxima.getDate()
          ) -
          Date.UTC(
            hoy.getFullYear(),
            hoy.getMonth(),
            hoy.getDate()
          )
        ) / 86400000
      );

      return {
        ...d,
        fechaUltimaMantencion: entrega,
        fechaProximaMantencion: proxima,
        diasRestantes
      };
    }).sort((a, b) => a.diasRestantes - b.diasRestantes);
  }, [docs]);

  const resumenMantencionesST = useMemo(() => {
    const hoy = new Date();

    return {
      total: mantencionesST.length,
      vencidas: mantencionesST.filter((m) => m.diasRestantes < 0),
      vencenHoy: mantencionesST.filter((m) => m.diasRestantes === 0),
      proximas7: mantencionesST.filter((m) =>
        m.diasRestantes > 0 && m.diasRestantes <= 7
      ),
      proximas15: mantencionesST.filter((m) =>
        m.diasRestantes > 0 && m.diasRestantes <= 15
      ),
      proximas30: mantencionesST.filter((m) =>
        m.diasRestantes > 0 && m.diasRestantes <= 30
      ),
      delMes: mantencionesST.filter((m) =>
        m.fechaProximaMantencion.getFullYear() === hoy.getFullYear() &&
        m.fechaProximaMantencion.getMonth() === hoy.getMonth()
      )
    };
  }, [mantencionesST]);

  const mantencionesSTFiltradas = useMemo(() => {
    const r = resumenMantencionesST;

    const mapa = {
      TODAS: mantencionesST,
      VENCIDAS: r.vencidas,
      HOY: r.vencenHoy,
      "7D": r.proximas7,
      "15D": r.proximas15,
      "30D": r.proximas30,
      MES: r.delMes,
      VENCIDAS_30D: mantencionesST.filter(
        (m) => m.diasRestantes <= 30
      )
    };

    const base = mapa[filtroMantencionST] || mantencionesST;
    const term = buscarMantencionST.trim().toLowerCase();

    if (!term) return base;

    return base.filter((m) =>
      [
        m.folio, m.rut, m.entidad, m.serie,
        m.tipo, m.marca, m.modelo,
        m.telefono, m.telefonos, m.correo
      ].some((v) =>
        String(v ?? "").toLowerCase().includes(term)
      )
    );
  }, [
    mantencionesST,
    resumenMantencionesST,
    filtroMantencionST,
    buscarMantencionST
  ]);

  const kpisEconomicos = useMemo(() => {
    const f0 = desdeEconomico ? startOfDay(desdeEconomico) : null;
    const f1 = hastaEconomico ? endOfDay(hastaEconomico) : null;

    const cabFiltrados = presupuestosCab.filter((p) => {
      const fecha =
        toDate(p.fecha_aceptado) ||
        toDate(p.fecha_generado) ||
        toDate(p.fechamod) ||
        toDate(p.fechaadd);

      if (!fecha) return false;
      if (f0 && fecha < f0) return false;
      if (f1 && fecha > f1) return false;
      return true;
    });

    const aceptados = cabFiltrados.filter((p) =>
      p.aceptado === true &&
      p.confirmado === true &&
      normalize(p.estado) === "ACEPTADO"
    );

    const ids = new Set(aceptados.map((p) => p.id));

    const repuestos = presupuestosDet.filter((d) =>
      ids.has(d.id_cab_pre) &&
      normalize(d.categoria) === "REPUESTO"
    );

    const gastoEstimado = repuestos.reduce(
      (sum, d) => sum + (Number(d.price) || 0),
      0
    );

    const counter = {};

    repuestos.forEach((d) => {
      const name = String(d.item || "SIN NOMBRE").trim();

      if (!counter[name]) {
        counter[name] = {
          item: name,
          cantidad: 0,
          total: 0
        };
      }

      counter[name].cantidad += 1;
      counter[name].total += Number(d.price) || 0;
    });

    return {
      presupuestosGenerados: cabFiltrados.length,
      presupuestosAceptados: aceptados.length,
      porcentajeAceptacion: cabFiltrados.length
        ? aceptados.length * 100 / cabFiltrados.length
        : 0,
      itemsRepuestos: repuestos.length,
      gastoEstimado,
      ticketPromedio: aceptados.length
        ? gastoEstimado / aceptados.length
        : 0,
      topRepuestos: Object.values(counter)
        .sort((a, b) => b.cantidad - a.cantidad)
        .slice(0, 5)
    };
  }, [
    presupuestosCab,
    presupuestosDet,
    desdeEconomico,
    hastaEconomico
  ]);

  const doughnutServicio = useMemo(() => {
    const entries = Object.entries(agg.byServicio)
      .sort((a, b) => b[1] - a[1]);

    const values = entries.map(([, n]) => n);

    return {
      data: {
        labels: entries.map(([name]) => name),
        datasets: [{
          data: values,
          backgroundColor: entries.map(
            (_, i) => palette[i % palette.length]
          ),
          borderColor: "#fff",
          borderWidth: 2,
          hoverOffset: 8
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "58%",
        plugins: {
          legend: {
            position: "bottom",
            labels: { boxWidth: 12, padding: 12 }
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const total = values.reduce((a, b) => a + b, 0);
                const n = Number(ctx.raw) || 0;
                const pct = total ? (n * 100 / total).toFixed(1) : 0;
                return `${ctx.label}: ${n} (${pct}%)`;
              }
            }
          }
        }
      }
    };
  }, [agg.byServicio]);

  const barEstado = useMemo(
    () => makeBar(agg.byEstado, true),
    [agg.byEstado]
  );

  const barTecnico = useMemo(
    () => makeBar(agg.byTecnico, false, 8),
    [agg.byTecnico]
  );

  const exportExcel = () => {
    exportWorkbook(
      filtered.map((d) => ({
        Folio: d.folio ?? "",
        Rut: d.rut ?? "",
        Cliente: d.entidad ?? "",
        Correo: d.correo ?? "",
        Teléfono: d.telefono || d.telefonos || "",
        Dirección: d.direccion ?? "",
        Familia: d.familia ?? "",
        Tipo: d.tipo ?? "",
        Marca: d.marca ?? "",
        Modelo: d.modelo ?? "",
        Serie: d.serie ?? "",
        Servicio: d.servicio ?? "",
        Estado: d.estado ?? "",
        "Estado Servicio": d.estado_servicio ?? "",
        Técnico: d.tecnico ?? "",
        Confirmado: d.confirmado ? "Sí" : "No",
        Ingreso: formatCL(d.date),
        Cierre: formatCL(d.fecha_out)
      })),
      "Órdenes ST",
      `Dashboard_ST_${dateInput(new Date())}.xlsx`
    );
  };

  const exportMantencionesSTExcel = () => {
    exportWorkbook(
      mantencionesSTFiltradas.map((m) => ({
        Folio: m.folio ?? "",
        Rut: m.rut ?? "",
        Cliente: m.entidad ?? "",
        Teléfonos: m.telefono || m.telefonos || "",
        Email: m.correo ?? "",
        Familia: m.familia ?? "",
        Tipo: m.tipo ?? "",
        Marca: m.marca ?? "",
        Modelo: m.modelo ?? "",
        Serie: m.serie ?? "",
        Servicio: m.servicio ?? "",
        Técnico: m.tecnico ?? "",
        Estado: m.estado ?? "",
        "Estado servicio": m.estado_servicio ?? "",
        "Última mantención": formatCL(m.fechaUltimaMantencion),
        "Próxima mantención": formatCL(m.fechaProximaMantencion),
        "Días restantes": m.diasRestantes,
        Situación: m.diasRestantes < 0
          ? `Vencida hace ${Math.abs(m.diasRestantes)} días`
          : m.diasRestantes === 0
            ? "Vence hoy"
            : m.diasRestantes <= 30
              ? `Por vencer en ${m.diasRestantes} días`
              : `Vigente, vence en ${m.diasRestantes} días`
      })),
      "Mantenciones ST",
      `Mantenciones_ST_${filtroMantencionST}_${dateInput(new Date())}.xlsx`
    );
  };

  const operationPane = (
    <Tab.Pane style={{
      background: "transparent",
      border: 0,
      padding: "16px 0"
    }}>
      <Segment style={cardStyle}>
        <SectionTitle
          icon="filter"
          title="Filtros operacionales"
          subtitle={`Órdenes filtradas: ${agg.total}`}
          action={
            <Button basic color="grey" size="small" onClick={resetOperacion}>
              <Icon name="undo" /> Limpiar
            </Button>
          }
        />
        <Divider />
        <Form>
          <Form.Group widths="equal">
            <Form.Field>
              <label>Período</label>
              <Dropdown
                fluid
                selection
                value={preset}
                options={
                  preset === "PERSONALIZADO"
                    ? [{
                        key: "PERSONALIZADO",
                        value: "PERSONALIZADO",
                        text: "Personalizado"
                      }, ...rangeOptions]
                    : rangeOptions
                }
                onChange={(_, data) => changePreset(data.value)}
              />
            </Form.Field>
            <Form.Field>
              <label>Desde</label>
              <Input
                fluid
                type="date"
                value={dateInput(fromDate)}
                onChange={(e) => {
                  setFromDate(parseLocalDate(e.target.value));
                  setPreset("PERSONALIZADO");
                }}
              />
            </Form.Field>
            <Form.Field>
              <label>Hasta</label>
              <Input
                fluid
                type="date"
                value={dateInput(toDateValue)}
                onChange={(e) => {
                  setToDateValue(parseLocalDate(e.target.value));
                  setPreset("PERSONALIZADO");
                }}
              />
            </Form.Field>
            <Form.Field>
              <label>Buscar</label>
              <Input
                fluid
                icon="search"
                placeholder="Folio, RUT, serie, cliente..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Form.Field>
          </Form.Group>
          <Form.Group widths="equal">
            <FilterDropdown
              label="Estado"
              value={estado}
              onChange={setEstado}
              values={options.estados}
            />
            <FilterDropdown
              label="Estado servicio"
              value={estadoServicio}
              onChange={setEstadoServicio}
              values={options.estadosServicio}
            />
            <FilterDropdown
              label="Técnico"
              value={tecnico}
              onChange={setTecnico}
              values={options.tecnicos}
            />
            <FilterDropdown
              label="Servicio"
              value={servicio}
              onChange={setServicio}
              values={options.servicios}
            />
            <FilterDropdown
              label="Tipo"
              value={tipo}
              onChange={setTipo}
              values={options.tipos}
            />
          </Form.Group>
        </Form>
      </Segment>

      <Segment style={cardStyle}>
        <SectionTitle
          icon="dashboard"
          title="Indicadores operacionales"
          subtitle="Órdenes del período seleccionado, excluyendo anuladas"
          action={
            <Button
              color="green"
              size="small"
              disabled={!filtered.length}
              onClick={exportExcel}
            >
              <Icon name="file excel outline" /> Exportar Excel
            </Button>
          }
        />
        <Card.Group itemsPerRow={5} stackable doubling>
          <KpiCard
            label="Total órdenes"
            value={agg.total}
            icon="clipboard list"
          />
          <KpiCard
            label="Entregadas"
            value={agg.entregadas}
            icon="check circle"
            color="green"
            detail={`${agg.pctEntregadas.toFixed(1)}% del total`}
          />
          <KpiCard
            label="En ejecución"
            value={agg.enEjecucion}
            icon="settings"
            color="orange"
          />
          <KpiCard
            label="Pendientes"
            value={agg.pendientes}
            icon="clock outline"
            color="blue"
          />
          <KpiCard
            label="Promedio cierre"
            value={`${agg.promCierre.toFixed(2)} días`}
            icon="calendar check outline"
            color="violet"
          />
        </Card.Group>
      </Segment>

      <Grid stackable columns={2}>
        <Grid.Column>
          <ChartPanel
            title="Distribución por servicio"
            empty={!agg.total}
          >
            <Doughnut
              data={doughnutServicio.data}
              options={doughnutServicio.options}
            />
          </ChartPanel>
        </Grid.Column>
        <Grid.Column>
          <ChartPanel
            title="Órdenes por estado"
            empty={!agg.total}
          >
            <Bar data={barEstado.data} options={barEstado.options} />
          </ChartPanel>
        </Grid.Column>
      </Grid>

      <Segment style={{ ...cardStyle, marginTop: 16 }}>
        <ChartPanel
          title="Top técnicos por órdenes"
          empty={!agg.total}
          height={290}
        >
          <Bar data={barTecnico.data} options={barTecnico.options} />
        </ChartPanel>
      </Segment>

      <Segment style={cardStyle}>
        <SectionTitle
          icon="list"
          title="Detalle de órdenes"
          subtitle={`Últimos ${Math.min(filtered.length, 30)} registros de ${filtered.length}`}
        />
        {!filtered.length ? (
          <Message
            info
            content="No hay registros con los filtros seleccionados."
          />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <Table compact celled selectable striped unstackable size="small">
              <Table.Header>
                <Table.Row>
                  {[
                    "Folio", "Cliente", "Equipo", "Serie", "Servicio",
                    "Estado", "Estado servicio", "Técnico", "Ingreso", "Cierre"
                  ].map((h) => (
                    <Table.HeaderCell key={h}>{h}</Table.HeaderCell>
                  ))}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filtered.slice()
                  .sort((a, b) =>
                    Number(b.folio || 0) - Number(a.folio || 0)
                  )
                  .slice(0, 30)
                  .map((d) => (
                    <Table.Row key={d.id}>
                      <Table.Cell>{d.folio}</Table.Cell>
                      <Table.Cell>
                        <strong>{d.entidad}</strong>
                        <div style={{ color: UI.muted, fontSize: 12 }}>
                          {d.rut}
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        {d.tipo}
                        <div style={{ color: UI.muted, fontSize: 12 }}>
                          {d.marca} {d.modelo}
                        </div>
                      </Table.Cell>
                      <Table.Cell>{d.serie}</Table.Cell>
                      <Table.Cell>{d.servicio}</Table.Cell>
                      <Table.Cell>
                        <StatusLabel value={d.estado} />
                      </Table.Cell>
                      <Table.Cell>{d.estado_servicio}</Table.Cell>
                      <Table.Cell>{d.tecnico}</Table.Cell>
                      <Table.Cell>{formatCL(d.date)}</Table.Cell>
                      <Table.Cell>{formatCL(d.fecha_out)}</Table.Cell>
                    </Table.Row>
                  ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </Segment>
    </Tab.Pane>
  );

  const economicPane = (
    <Tab.Pane style={{
      background: "transparent",
      border: 0,
      padding: "16px 0"
    }}>
      <Segment style={cardStyle}>
        <SectionTitle
          icon="filter"
          title="Filtros económicos"
          subtitle="Período independiente de las órdenes operacionales"
          action={
            <Button
              basic
              color="grey"
              size="small"
              onClick={resetEconomico}
            >
              <Icon name="undo" /> Limpiar
            </Button>
          }
        />
        <Divider />
        <Form>
          <DateFilters
            preset={presetEconomico}
            from={desdeEconomico}
            to={hastaEconomico}
            onPreset={changeEconomicPreset}
            onFrom={(v) => {
              setDesdeEconomico(parseLocalDate(v));
              setPresetEconomico("PERSONALIZADO");
            }}
            onTo={(v) => {
              setHastaEconomico(parseLocalDate(v));
              setPresetEconomico("PERSONALIZADO");
            }}
          />
        </Form>
      </Segment>

      <Segment style={cardStyle}>
        <SectionTitle
          icon="dollar sign"
          title="Indicadores económicos estimados"
          subtitle="Presupuestos aceptados y repuestos asociados al período"
        />
        <Card.Group itemsPerRow={5} stackable doubling>
          <KpiCard
            label="Aceptados"
            value={kpisEconomicos.presupuestosAceptados}
            icon="check circle"
            color="green"
          />
          <KpiCard
            label="Aceptación"
            value={`${kpisEconomicos.porcentajeAceptacion.toFixed(1)}%`}
            icon="percent"
            color="blue"
          />
          <KpiCard
            label="Ítems repuestos"
            value={kpisEconomicos.itemsRepuestos}
            icon="wrench"
            color="orange"
          />
          <KpiCard
            label="Valor Venta de repuestos aprobados"
            value={money(kpisEconomicos.gastoEstimado)}
            icon="dollar sign"
            color="red"
          />
          <KpiCard
            label="Venta Promedio repuestos por presupuesto"
            value={money(kpisEconomicos.ticketPromedio)}
            icon="calculator"
          />
        </Card.Group>
      </Segment>

      <Segment style={cardStyle}>
        <SectionTitle
          icon="wrench"
          title="Top repuestos presupuestados"
          subtitle="Repuestos de presupuestos aceptados"
        />
        <div style={{ overflowX: "auto" }}>
          <Table compact celled striped unstackable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Repuesto</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">Cantidad</Table.HeaderCell>
                <Table.HeaderCell textAlign="right">
                  Total estimado
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {!kpisEconomicos.topRepuestos.length ? (
                <Table.Row>
                  <Table.Cell colSpan="3" textAlign="center">
                    Sin repuestos aceptados
                  </Table.Cell>
                </Table.Row>
              ) : (
                kpisEconomicos.topRepuestos.map((item) => (
                  <Table.Row key={item.item}>
                    <Table.Cell>{item.item}</Table.Cell>
                    <Table.Cell textAlign="right">
                      {item.cantidad}
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      {money(item.total)}
                    </Table.Cell>
                  </Table.Row>
                ))
              )}
            </Table.Body>
          </Table>
        </div>
      </Segment>
    </Tab.Pane>
  );

  const maintenancePane = (
    <Tab.Pane style={{
      background: "transparent",
      border: 0,
      padding: "16px 0"
    }}>
      <Segment style={cardStyle}>
        <SectionTitle
          icon="calendar check outline"
          title="Seguimiento de mantenciones ST"
          subtitle="Próximas mantenciones anuales desde la última fecha de entrega"
        />
        <Card.Group itemsPerRow={5} stackable doubling>
          <KpiCard
            label="Mantenciones realizadas"
            value={resumenMantencionesST.total}
            icon="clipboard list"
            color="blue"
          />
          <KpiCard
            label="Vencidas"
            value={resumenMantencionesST.vencidas.length}
            icon="exclamation triangle"
            color="red"
          />
          <KpiCard
            label="Próximos 7 días"
            value={resumenMantencionesST.proximas7.length}
            icon="clock outline"
            color="orange"
          />
          <KpiCard
            label="Próximos 30 días"
            value={resumenMantencionesST.proximas30.length}
            icon="calendar alternate outline"
            color="yellow"
          />
          <KpiCard
            label="Vencen este mes"
            value={resumenMantencionesST.delMes.length}
            icon="calendar outline"
          />
        </Card.Group>
      </Segment>

      <Segment style={cardStyle}>
        <SectionTitle
          icon="filter"
          title="Filtros de mantenciones"
          subtitle="Búsqueda y vencimientos independientes del resto del dashboard"
        />
        <Divider />

        <Grid stackable verticalAlign="bottom">
          <Grid.Column computer={6} tablet={8} mobile={16}>
            <Form>
              <Form.Field>
                <label>Situación de mantención</label>
                <Dropdown
                  fluid
                  selection
                  value={filtroMantencionST}
                  options={maintenanceOptions}
                  onChange={(_, data) =>
                    setFiltroMantencionST(data.value)
                  }
                />
              </Form.Field>
            </Form>
          </Grid.Column>

          <Grid.Column computer={6} tablet={8} mobile={16}>
            <Form>
              <Form.Field>
                <label>Buscar cliente o equipo</label>
                <Input
                  fluid
                  icon="search"
                  placeholder="Cliente, RUT, folio, serie..."
                  value={buscarMantencionST}
                  onChange={(e) =>
                    setBuscarMantencionST(e.target.value)
                  }
                />
              </Form.Field>
            </Form>
          </Grid.Column>

          <Grid.Column
            computer={4}
            tablet={16}
            mobile={16}
            textAlign="right"
          >
            <Button
              basic
              color="grey"
              size="small"
              onClick={resetMantenciones}
            >
              <Icon name="undo" /> Limpiar
            </Button>
            <Button
              color="green"
              size="small"
              disabled={!mantencionesSTFiltradas.length}
              onClick={exportMantencionesSTExcel}
            >
              <Icon name="file excel outline" /> Excel
            </Button>
          </Grid.Column>
        </Grid>
      </Segment>

      <Segment style={cardStyle}>
        <SectionTitle
          icon="list alternate outline"
          title="Órdenes de mantención"
          subtitle={`${mantencionesSTFiltradas.length} equipos en el listado`}
        />

        {!mantencionesSTFiltradas.length ? (
          <Message
            info
            content="No existen mantenciones con los filtros seleccionados."
          />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <Table
              compact
              celled
              striped
              selectable
              unstackable
              size="small"
            >
              <Table.Header>
                <Table.Row>
                  {[
                    "Folio", "Cliente", "Equipo", "Serie",
                    "Última mantención", "Próxima mantención", "Situación"
                  ].map((h) => (
                    <Table.HeaderCell key={h}>{h}</Table.HeaderCell>
                  ))}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {mantencionesSTFiltradas.map((m) => (
                  <Table.Row key={m.id}>
                    <Table.Cell>{m.folio}</Table.Cell>
                    <Table.Cell>
                      <strong>{m.entidad || "Sin cliente"}</strong>
                      <div style={{
                        color: UI.muted,
                        fontSize: 12
                      }}>
                        {m.rut || ""}
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      {m.tipo || "Sin tipo"}
                      <div style={{
                        color: UI.muted,
                        fontSize: 12
                      }}>
                        {m.marca} {m.modelo}
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      {m.serie || "Sin serie"}
                    </Table.Cell>
                    <Table.Cell>
                      {formatCL(m.fechaUltimaMantencion, true)}
                    </Table.Cell>
                    <Table.Cell>
                      {formatCL(m.fechaProximaMantencion, true)}
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <MaintenanceLabel days={m.diasRestantes} />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </Segment>
    </Tab.Pane>
  );

  return (
    <div style={{
      marginLeft: 72,
      padding: "22px 22px 36px",
      minHeight: "calc(100vh - 70px)",
      background: UI.bg
    }}>
      <Segment style={{
        ...cardStyle,
        padding: "20px 24px"
      }}>
        <Grid stackable verticalAlign="middle">
          <Grid.Column width={12}>
            <Header as="h2" style={{
              color: UI.navy,
              margin: 0
            }}>
              <Icon name="chart line" color="teal" />
              <Header.Content>
                Dashboard Servicio Técnico
                <Header.Subheader style={{
                  color: UI.muted,
                  marginTop: 7
                }}>
                  Gestión operacional, presupuestos y mantenciones
                </Header.Subheader>
              </Header.Content>
            </Header>
          </Grid.Column>
          <Grid.Column width={4} textAlign="right">
            <Button
              basic
              color="teal"
              icon
              labelPosition="left"
              loading={loading}
              disabled={loading || !empId}
              onClick={load}
            >
              <Icon name="refresh" />
              Refrescar
            </Button>
          </Grid.Column>
        </Grid>
      </Segment>

      {error && (
        <Message negative icon="warning sign" content={error} />
      )}

      <div style={{ position: "relative" }}>
        {loading && (
          <Dimmer active inverted>
            <Loader>Procesando...</Loader>
          </Dimmer>
        )}

        <Tab
          menu={{
            secondary: true,
            pointing: true,
            color: "teal",
            stackable: true
          }}
          panes={[
            {
              menuItem: {
                key: "operacion",
                icon: "dashboard",
                content: "Operación"
              },
              render: () => operationPane
            },
            {
              menuItem: {
                key: "economico",
                icon: "dollar sign",
                content: "Presupuestos y Repuestos"
              },
              render: () => economicPane
            },
            {
              menuItem: {
                key: "mantenciones",
                icon: "calendar check outline",
                content: "Mantenciones ST"
              },
              render: () => maintenancePane
            }
          ]}
        />
      </div>
    </div>
  );
};

export default DashboardServicioTecnico;
